import {
  Box,
  Flex,
  FormControl,
  FormControlProps,
  FormErrorMessage,
  FormLabel,
  FormLabelProps,
  VStack,
} from '@chakra-ui/react';
import { formatInTimeZone, zonedTimeToUtc } from 'date-fns-tz';
import { DateTime } from 'luxon';
import React, { useMemo, useState } from 'react';
import DatePicker, { CalendarContainer } from 'react-datepicker';

import { date as commonDate } from '@finley-technologies/common';

import 'react-datepicker/dist/react-datepicker.css';
import { useSimpleColorMode } from '~/packages/common-frontend/theme/utils';

const offsetDate = (date: Date, tz: string): Date => {
  return new Date(
    DateTime.fromFormat(
      formatInTimeZone(date, tz, 'yyyy-MM-dd HH:mm:ss'),
      'yyyy-MM-dd HH:mm:ss',
    ).toISO(),
  );
};

const offsetMaybeDate = (
  date: Date | undefined | null,
  tz: string,
): Date | undefined => (date ? offsetDate(date, tz) : undefined);

type SimpleDatePickerProps = {
  minDate?: Date | null;
  maxDate?: Date | null;
  selectedDate?: Date | null;
  setSelectedDate:
    | React.Dispatch<React.SetStateAction<Date | null>>
    | ((value: Date | null) => void);
  dateFormat?: string;
  width?: string;
  showTimeSelect?: boolean;
  tz?: string;
  placeHolder?: string;
  disabled?: boolean;
  errorMessage?: string;
  excludeHolidays?: boolean;
  excludeWeekends?: boolean;
  formControlProps?: FormControlProps;
  formLabelProps?: FormLabelProps;
  isInvalid?: boolean;
  isSelectedDateOptional?: boolean;
  label?: string;
};

const DEFAULT_MIN_YEAR = new Date().getFullYear() - 5;
const DEFAULT_MAX_YEAR = new Date().getFullYear() + 5;

export const SimpleDatePicker = ({
  minDate,
  maxDate,
  selectedDate,
  setSelectedDate,
  dateFormat = 'yyyy-MM-dd',
  width = 'sm',
  showTimeSelect,
  tz = 'America/New_York',
  placeHolder,
  disabled,
  errorMessage = 'Select a valid date',
  excludeHolidays = false,
  excludeWeekends = false,
  formControlProps,
  formLabelProps,
  isInvalid = false,
  isSelectedDateOptional = false,
  label,
}: Omit<SimpleDatePickerProps, 'showIcon'>) => {
  // DatePicker only renders in browser local time, requiring adjustment
  const offsetSelectedDate = offsetMaybeDate(selectedDate, tz);
  const offsetMinDate = offsetMaybeDate(minDate, tz);
  const offsetMaxDate = offsetMaybeDate(maxDate, tz);

  const [isInputInvalid, setIsInputInvalid] = useState(false);
  const minYear = minDate ? new Date(minDate).getFullYear() : DEFAULT_MIN_YEAR;
  const maxYear = maxDate ? new Date(maxDate).getFullYear() : DEFAULT_MAX_YEAR;

  const YEARS_TO_INCLUDE = useMemo(() => {
    return Array.from({ length: maxYear - minYear + 1 }, (_, i) => {
      return minYear + i;
    });
  }, [minYear, maxYear]);

  const excludeHolidaysDates = useMemo(() => {
    return excludeHolidays
      ? YEARS_TO_INCLUDE.flatMap((year) =>
          commonDate.USFederalHolidays.map((holiday) =>
            holiday(year).toJSDate(),
          ),
        )
      : undefined;
  }, [excludeHolidays, YEARS_TO_INCLUDE]);

  const mode = useSimpleColorMode();
  const bgColor = mode('white', 'gray.950');
  const borderColor = isInvalid
    ? mode('red.200', 'red.600')
    : mode('gray.200', 'gray.600');
  const opacity = disabled ? 0.4 : 1;

  return (
    <Flex w={width} cursor={disabled ? 'not-allowed' : 'default'}>
      <FormControl
        alignItems="start"
        display="flex"
        isInvalid={isInputInvalid || isInvalid}
        m="0rem"
        {...formControlProps}
      >
        {label && (
          <FormLabel m="0" w="40%" pt="0.5rem" {...formLabelProps}>
            {label}
          </FormLabel>
        )}
        <VStack w="100%" alignItems="flex-start" gap="0">
          <Flex
            className="fullDatePicker-main-container"
            sx={{
              alignItems: 'center',
              bgColor,
              borderRadius: '0.375rem',
              borderWidth: '1px',
              height: '40px',
              opacity,
            }}
            borderColor={borderColor}
            width="100%"
          >
            <DatePicker
              selected={isInputInvalid ? null : offsetSelectedDate}
              onChange={(date: Date | null) => {
                // TODO: @markhaghani - handle date search based on Date Format
                if (isSelectedDateOptional) {
                  setSelectedDate(date ? zonedTimeToUtc(date, tz) : null);
                } else if (!isSelectedDateOptional && date === null) {
                  setIsInputInvalid(true);
                  setSelectedDate(null);
                } else if (!isSelectedDateOptional && date !== null) {
                  setIsInputInvalid(false);
                  setSelectedDate(zonedTimeToUtc(date, tz));
                }
              }}
              minDate={offsetMinDate}
              maxDate={offsetMaxDate}
              dateFormat={dateFormat}
              showTimeSelect={showTimeSelect}
              placeholderText={placeHolder}
              calendarContainer={({ children }) => (
                <CalendarContainer className="single-datepicker-calendar-container">
                  <Box position="relative">{children}</Box>
                </CalendarContainer>
              )}
              disabled={disabled}
              disabledKeyboardNavigation
              excludeDates={excludeHolidaysDates}
              filterDate={
                excludeWeekends
                  ? (date) => {
                      const dayOfWeek = date.getDay();
                      return !(dayOfWeek === 6 || dayOfWeek === 0);
                    }
                  : undefined
              }
              showIcon
            />
          </Flex>
          <FormErrorMessage>{errorMessage}</FormErrorMessage>
        </VStack>
      </FormControl>
    </Flex>
  );
};
